import { CONFERENCE_JOINED, CONFERENCE_LEFT } from '../base/conference/actionTypes';
import { getCurrentConference } from '../base/conference/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { fetchSessionStatus } from './api';
import { isClinicalSessionRoom } from './utils';
import { startSessionPolling, stopSessionPolling, updateSessionStatus, sessionEndedByTherapist, sessionStatusCheckFailed } from './actions';
/**
 * Middleware for session management - monitors clinical sessions for remote termination.
 * When a therapist ends a session from the dashboard, participants are gracefully disconnected.
 */
let pollingIntervalId = null;
let currentSessionId = null;
let lastKnownStatus = null;
/**
 * Stop the current polling interval if active.
 */
function stopPolling() {
    if (pollingIntervalId) {
        console.log('[session-management] Stopping session polling');
        clearInterval(pollingIntervalId);
        pollingIntervalId = null;
        currentSessionId = null;
        lastKnownStatus = null;
    }
}
/**
 * Start polling for session status updates.
 *
 * @param {Store} store - Redux store.
 * @param {string} roomName - The Jitsi room name (e.g., "session-927f96c7").
 * @param {string} backendUrl - The backend URL for API calls.
 * @param {number} pollInterval - Polling interval in milliseconds.
 */
function startPolling(store, roomName, backendUrl, pollInterval) {
    // Stop any existing polling
    stopPolling();
    currentSessionId = roomName;
    console.log(`[session-management] Starting session polling for room: ${roomName}`);
    // Dispatch action to track polling state
    store.dispatch(startSessionPolling(roomName, pollInterval));
    // Start the polling interval
    pollingIntervalId = setInterval(async () => {
        try {
            const sessionData = await fetchSessionStatus(roomName, backendUrl);
            // Update status in Redux if changed
            if (sessionData.status !== lastKnownStatus) {
                console.log(`[session-management] Session status changed: ${lastKnownStatus} -> ${sessionData.status}`);
                lastKnownStatus = sessionData.status;
                store.dispatch(updateSessionStatus(sessionData.id, sessionData.status));
                // Check if session has been completed
                if (sessionData.status === 'completed') {
                    console.log('[session-management] Session completed by therapist, disconnecting...');
                    // Dispatch session ended action
                    store.dispatch(sessionEndedByTherapist(sessionData.id));
                    // Stop polling
                    stopPolling();
                    // Show notification to user
                    store.dispatch(showNotification({
                        titleKey: 'Session Ended',
                        description: 'The therapist has ended this session. You will be disconnected in 3 seconds.',
                        uid: 'session-ended-notification',
                        appearance: 'warning'
                    }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
                    // Wait 3 seconds before disconnecting to allow user to see the notification
                    setTimeout(() => {
                        console.log('[session-management] Triggering conference hangup');
                        // Trigger hangup using the global APP API
                        if (typeof APP !== 'undefined' && APP.conference) {
                            APP.conference.hangup();
                        }
                        else {
                            console.error('[session-management] APP.conference not available');
                        }
                    }, 3000);
                }
            }
        }
        catch (error) {
            console.error('[session-management] Error checking session status:', error);
            store.dispatch(sessionStatusCheckFailed(error));
        }
    }, pollInterval);
}
/**
 * Middleware implementation.
 */
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);
    const state = store.getState();
    switch (action.type) {
        case CONFERENCE_JOINED: {
            // Get the room name from the conference
            const conference = action.conference || getCurrentConference(state);
            if (conference) {
                const roomName = conference.getName();
                console.log(`[session-management] Conference joined: ${roomName}`);
                // Check if this is a clinical session room
                if (isClinicalSessionRoom(roomName)) {
                    // Get configuration
                    const config = state['features/base/config'];
                    const sessionManagementConfig = config?.sessionManagement || {};
                    const enabled = sessionManagementConfig.enabled !== false;
                    const backendUrl = sessionManagementConfig.backendUrl || 'https://app.nor-dmt.no/api/clinical-admin';
                    const pollInterval = sessionManagementConfig.pollInterval || 5000;
                    if (enabled) {
                        console.log(`[session-management] Clinical session detected: ${roomName}, starting monitoring`);
                        startPolling(store, roomName, backendUrl, pollInterval);
                    }
                    else {
                        console.log('[session-management] Session management is disabled in config');
                    }
                }
            }
            break;
        }
        case CONFERENCE_LEFT: {
            console.log('[session-management] Conference left, stopping polling');
            stopPolling();
            store.dispatch(stopSessionPolling());
            break;
        }
    }
    return result;
});
